/**
 * Sign-out orchestration.
 *
 * The two halves of a sign-out are not interchangeable, and conflating them is a
 * security bug rather than a cosmetic one:
 *
 *  1. `POST /auth/logout` asks the API to revoke the session token. It needs the
 *     CSRF token plus the session and CSRF cookies, and deliberately no tenant
 *     header — a session is not scoped to one tenant.
 *  2. Only then is local state cleared: the CSRF token, the remembered profile, the
 *     selected tenant scope and every cached tenant row.
 *
 * If step 1 fails the cookie may still be valid on the server, so step 2 still runs
 * (the operator asked to leave and local credentials must not linger) but the
 * result is reported as `local-only` and the UI says the server session was not
 * ended. An earlier revision only did step 2 and always looked successful, which
 * misrepresented a still-live session as a completed sign-out.
 */

import { logout } from '../api/client';
import { isApiError, type ApiError } from '../api/errors';
import { sessionStore, type SignOutResult } from './session';

function asApiError(error: unknown): ApiError | null {
  return isApiError(error) ? error : null;
}

/**
 * Revokes the session server-side, then clears local state.
 *
 * Never throws: the caller renders the outcome. A failure to reach the API is a
 * result to display, not an exception that would abort the local cleanup.
 */
export async function performSignOut(): Promise<SignOutResult> {
  let error: ApiError | null = null;

  try {
    await logout();
  } catch (cause) {
    error = asApiError(cause);
    if (error === null) {
      // A non-ApiError escaping the transport is unexpected; record its message so
      // the notice still says something specific instead of a generic failure.
      error = null;
      console.warn('[yq] 退出登录时发生未预期的错误。', cause);
    }
  }

  sessionStore.signOut();

  return { outcome: error === null ? 'revoked' : 'local-only', error };
}

/** Chinese copy for the sign-out failure notice. */
export function describeSignOutFailure(error: ApiError | null): string {
  if (error === null) {
    return '已清除本机登录状态，但未能确认服务端会话是否已撤销。请关闭浏览器或联系管理员核查。';
  }

  if (error.kind === 'network') {
    return '无法连接服务，服务端会话可能仍然有效。本机已清除登录状态；如为共用设备，请关闭浏览器或联系管理员撤销会话。';
  }

  // The server's own message is English and names the endpoint; it is not shown.
  return `服务端撤销会话失败${error.status === undefined ? '' : `（状态码 ${String(error.status)}）`}。本机已清除登录状态，但服务端会话可能仍然有效；如为共用设备，请关闭浏览器或联系管理员撤销会话。`;
}
